// EXEMPT
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "goober";
import { Button } from "clutch/src/Button/Button.jsx";
import { TextArea } from "clutch/src/TextArea/TextArea.jsx";
import { TextField } from "clutch/src/TextField/TextField.jsx";

import { readState } from "@/__main__/app-state.mjs";
import blitzMessage, { EVENTS } from "@/__main__/ipc-core.mjs";
import { setShowFeedbackModal } from "@/app/actions.mjs";
import { GAME_SYMBOL_BY_SHORT_NAME } from "@/app/constants.mjs";
import FeedbackModalRefs from "@/app/FeedbackModal.refs.jsx";
import { GAME_SYMBOL_LOL } from "@/game-lol/definition-symbol.mjs";
import { GAME_SYMBOL_VAL } from "@/game-val/definition-symbol.mjs";
import type { FullModalRefOptions } from "@/shared/FullModal.jsx";
import FullModal from "@/shared/FullModal.jsx";
import globals from "@/util/global-whitelist.mjs";
import { useSnapshot } from "@/util/use-snapshot.mjs";

declare module "@/__main__/app-state.mjs" {
  interface VolatileState {
    feedbackModalOpen?: boolean;
  }
}

export default function FeedbackModal() {
  const { t } = useTranslation();
  const feedbackModalRef = useRef<FullModalRefOptions>(null);
  const [discordUsername, setDiscordUsername] = useState("");
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [submitButtonText, setSubmitButtonText] = useState(
    t("common:feedback.submit", "Submit"),
  );
  const [enableSubmit, setEnableSubmit] = useState(false);
  const { user, settings, volatile } = useSnapshot(readState);

  const MIN_FEEDBACK_LENGTH = 20;

  const onChangeDiscord = useCallback(
    (event) => {
      setDiscordUsername(event.target.value);
    },
    [setDiscordUsername],
  );

  const onChangeMessage = useCallback(
    (event) => {
      setFeedbackMessage(event.target.value);
      if (event.target.value.length >= MIN_FEEDBACK_LENGTH) {
        setEnableSubmit(true);
      } else {
        setEnableSubmit(false);
      }
    },
    [setFeedbackMessage],
  );

  const onSubmit = useCallback(
    (e) => {
      e.preventDefault();
      const RITO_GAMES = [GAME_SYMBOL_LOL, GAME_SYMBOL_VAL];

      const additionalInfo = [
        {
          title: "User Accounts",
          value: Object.entries(settings?.lastLoggedInIdByGame)
            .filter(([, id]) => Boolean(id))
            .map(([game, id]) => {
              if (RITO_GAMES.includes(GAME_SYMBOL_BY_SHORT_NAME[game])) {
                const account = settings?.loggedInAccounts?.[game]?.[id] ?? {};
                const { gameName, tagLine } = account;
                if (gameName && tagLine) {
                  return `[${game.toUpperCase()}] ${gameName}#${tagLine}`;
                }
              }
              return `[${game.toUpperCase()}] ${id}`;
            })
            .join("; "),
        },
        { title: "Message", value: feedbackMessage },
        { title: "Email", value: user?.email || "" },
        {
          title: "Roles",
          value: user?.roles?.map(({ code }) => code).join(", ") || "NONE",
        },
        { title: "Discord", value: discordUsername },
        {
          title: "App route",
          value: decodeURIComponent(globals.location?.pathname),
        },
        {
          title: "Session ID Logs",
          value: FeedbackModalRefs.getLogsSessionId(),
        },
        { title: "Session ID RUM", value: FeedbackModalRefs.getRumSessionId() },
      ].filter((info) => Boolean(info.value));

      setSubmitButtonText(
        t("common:feedback.thanksForFeedback", "Thanks for your feedback!"),
      );
      setEnableSubmit(false);
      blitzMessage(EVENTS.SEND_FEEDBACK, additionalInfo);
      setTimeout(() => feedbackModalRef.current.close(), 1000);
    },
    [
      discordUsername,
      feedbackMessage,
      settings?.lastLoggedInIdByGame,
      settings?.loggedInAccounts,
      t,
      user?.email,
      user?.roles,
    ],
  );

  const handleResetForm = useCallback(() => {
    setDiscordUsername("");
    setFeedbackMessage("");
    setSubmitButtonText(t("common:feedback.submit", "Submit"));
    setEnableSubmit(false);
    setShowFeedbackModal(false);
  }, [t]);

  useEffect(() => {
    if (feedbackModalRef.current && volatile.feedbackModalOpen) {
      feedbackModalRef.current.showModal();
    }
  }, [volatile.feedbackModalOpen]);

  return (
    <FullModal ref={feedbackModalRef} onModalClose={handleResetForm}>
      <Form onSubmit={onSubmit}>
        <h3>
          {t(
            "common:feedback.sendFeedbackOrReportBug",
            "Send feedback or report a bug",
          )}
        </h3>

        <TextField
          type="text"
          name="discord"
          placeholder={t("common:feedback.yourDiscordUsername")}
          onChange={onChangeDiscord}
          autoFocus
        />

        <TextArea
          className="text-area"
          cols={50}
          rows={9}
          name="message"
          minLength={MIN_FEEDBACK_LENGTH}
          placeholder={t(
            "common:feedback.whatLikeToTell",
            "What would you like to tell us?",
          )}
          required
          onChange={onChangeMessage}
        >
          {feedbackMessage.length < MIN_FEEDBACK_LENGTH ? (
            <span
              data-tooltip={t(
                "common:minCharacters",
                "Min {{count}} characters",
                {
                  count: 20,
                },
              )}
              className="character-indicator"
            >
              -{MIN_FEEDBACK_LENGTH - feedbackMessage.length}
            </span>
          ) : null}
        </TextArea>

        <Button
          disabled={!enableSubmit}
          type="submit"
          emphasis="high"
          size="large"
        >
          {submitButtonText}
        </Button>
      </Form>
    </FullModal>
  );
}

const Form = styled("form")`
  display: flex;
  flex-direction: column;

  padding: var(--sp-6);
  background: var(--shade7);
  border-radius: var(--br);

  color: var(--shade1);

  > div,
  > input {
    width: 100%;

    margin-bottom: var(--sp-6);
  }

  > h3 {
    margin-bottom: var(--sp-6);
    text-align: center;
    color: var(--shade0);
  }

  > button {
    width: 100%;

    margin-bottom: 0;
  }

  .character-indicator {
    position: absolute;
    bottom: var(--sp-1);
    right: var(--sp-3);

    color: var(--red-50);
  }
`;
